// Meeting App Editor - Full page design for configuring calendar booking
import { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ArrowLeft, Calendar, Link2, Copy, Check, Eye, EyeOff, ExternalLink, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

// Generate a short alphanumeric code
const generatePrivateCode = () => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

export default function MeetingAppEditor({ 
  app, 
  onSave, 
  onClose,
  isOpen,
  pageSlug 
}) {
  const [config, setConfig] = useState(null);
  const [copied, setCopied] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (app) {
      setConfig({
        ...app,
        button_text: app.button_text || app.buttonText || 'Book a Meeting',
        calendar_url: app.calendar_url || '',
        hide_from_profile: app.hide_from_profile || false,
        private_code: app.private_code || generatePrivateCode(),
      });
    }
  }, [app]);

  if (!isOpen || !config) return null;

  const meetingUrl = `${window.location.origin}/p/${pageSlug}/meet/${config.private_code}`;

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(meetingUrl);
      setCopied(true);
      toast.success('Link copied!');
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleSave = async () => {
    if (!config.calendar_url) {
      toast.error('Please enter your calendar link');
      return;
    }
    setSaving(true);
    try {
      await onSave({
        ...config,
        enabled: true
      });
      toast.success('Meeting settings saved!');
    } catch (err) {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const regenerateCode = () => {
    setConfig(prev => ({ ...prev, private_code: generatePrivateCode() }));
    toast.success('New private link generated');
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-[#F8F9FB] flex flex-col">
      {/* Header */}
      <header className="h-14 bg-white border-b border-gray-200 flex items-center justify-between px-5 flex-shrink-0">
        <div className="flex items-center gap-3">
          <button 
            onClick={onClose} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            data-testid="meeting-editor-back"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="h-6 w-px bg-gray-200" />
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <Calendar className="w-4 h-4 text-white" />
            </div>
            <div>
              <h1 className="text-base font-semibold text-gray-900">Book a Meeting</h1>
              <p className="text-xs text-gray-500">Configure your calendar booking</p>
            </div>
          </div>
        </div>
        
        {/* Visibility Toggle - Center */}
        <div className="flex items-center gap-3">
          <span className="text-sm text-gray-600">Show on Profile</span>
          <button
            onClick={() => setConfig(prev => ({ ...prev, hide_from_profile: !prev.hide_from_profile }))}
            className={`relative w-12 h-6 rounded-full transition-colors duration-200 ${
              !config.hide_from_profile ? 'bg-blue-500' : 'bg-gray-300'
            }`}
            data-testid="meeting-visibility-toggle"
          >
            <span
              className={`absolute top-1 left-1 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${
                !config.hide_from_profile ? 'translate-x-6' : 'translate-x-0'
              }`}
            />
          </button>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            !config.hide_from_profile ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {!config.hide_from_profile ? 'ON' : 'OFF'}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          <Button 
            size="sm" 
            onClick={handleSave} 
            disabled={saving || !config.calendar_url} 
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="meeting-save-btn"
          >
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Panel - Settings */}
        <div className="w-[420px] bg-white border-r border-gray-200 flex flex-col flex-shrink-0 overflow-auto">
          <div className="p-6 space-y-6">
            {/* Calendar URL Section */}
            <div className="space-y-3">
              <label className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <Link2 className="w-4 h-4 text-gray-400" />
                Calendar Link
              </label>
              <Input
                value={config.calendar_url}
                onChange={(e) => setConfig(prev => ({ ...prev, calendar_url: e.target.value }))}
                placeholder="https://calendly.com/yourname"
                className="h-11 border-gray-200 focus:border-blue-400 focus:ring-blue-100"
                data-testid="meeting-calendar-url"
              />
              <p className="text-xs text-gray-400">
                Paste your Calendly, Cal.com, or any booking link
              </p>
            </div>

            {/* Button Text */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-gray-700">Button Text</label>
              <Input
                value={config.button_text}
                onChange={(e) => setConfig(prev => ({ ...prev, button_text: e.target.value }))}
                placeholder="Book a Meeting"
                className="h-11 border-gray-200 focus:border-blue-400 focus:ring-blue-100"
                data-testid="meeting-button-text"
              />
            </div>

            {/* Divider */}
            <div className="border-t border-gray-100" />

            {/* Private Link Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-gray-700">Private Booking Link</label>
                <button
                  onClick={regenerateCode}
                  className="flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-700 font-medium"
                  data-testid="meeting-regenerate-link"
                >
                  <RefreshCw className="w-3 h-3" />
                  Regenerate
                </button>
              </div>
              
              <div className="flex items-center gap-2">
                <div className="flex-1 flex items-center gap-2 px-3 py-2.5 bg-gray-50 border border-gray-200 rounded-lg">
                  <span className="text-sm text-gray-600 truncate flex-1 font-mono">
                    {meetingUrl}
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCopyUrl}
                  className="h-10 px-3 border-gray-200 hover:bg-gray-50"
                  data-testid="meeting-copy-link"
                >
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-500" />
                  )}
                </Button>
              </div>
              <p className="text-xs text-gray-400">
                Share this link directly — visitors can't guess it
              </p>
            </div>

            {/* Visibility Info Card */}
            <div className="p-4 bg-gray-50 rounded-xl border border-gray-100">
              <div className="flex items-start gap-3">
                {config.hide_from_profile ? (
                  <div className="w-9 h-9 rounded-lg bg-gray-200 flex items-center justify-center flex-shrink-0">
                    <EyeOff className="w-4 h-4 text-gray-500" />
                  </div>
                ) : (
                  <div className="w-9 h-9 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0">
                    <Eye className="w-4 h-4 text-blue-600" />
                  </div>
                )}
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {config.hide_from_profile ? 'Hidden from profile' : 'Visible on profile'}
                  </span>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {config.hide_from_profile 
                      ? 'Only accessible via the private link above' 
                      : 'Button will show on your public profile page'}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Right Panel - Preview */}
        <div className="flex-1 flex flex-col bg-[#F8F9FB] overflow-auto">
          <div className="p-6 flex-1">
            <div className="max-w-xl mx-auto">
              {/* Preview Header */}
              <div className="flex items-center gap-2 mb-4">
                <div className="w-2 h-2 rounded-full bg-blue-500" />
                <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Preview</span>
              </div>

              {/* Preview Card */}
              <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
                {/* Profile Header Mock */}
                <div className="p-8 border-b border-gray-100">
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 rounded-full bg-gradient-to-br from-blue-400 to-indigo-500" />
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">Your Name</h3>
                      <p className="text-sm text-gray-500">Your title at Company</p>
                    </div>
                  </div>
                </div>

                {/* Button Preview */}
                <div className="p-8">
                  <p className="text-sm text-gray-500 mb-4">How the button will appear:</p>
                  
                  {config.hide_from_profile ? (
                    <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
                      <div className="flex items-center gap-2 text-amber-700">
                        <EyeOff className="w-4 h-4" />
                        <span className="text-sm font-medium">Button is hidden from profile</span>
                      </div>
                      <p className="text-xs text-amber-600 mt-1">
                        Users can only access via the private link
                      </p>
                    </div>
                  ) : (
                    <a
                      href={config.calendar_url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-medium text-sm transition-all ${
                        config.calendar_url 
                          ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-md hover:shadow-lg' 
                          : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      }`}
                      onClick={(e) => !config.calendar_url && e.preventDefault()}
                    >
                      <Calendar className="w-4 h-4" />
                      {config.button_text || 'Book a Meeting'}
                    </a>
                  )}

                  {config.calendar_url && (
                    <div className="mt-6 pt-6 border-t border-gray-100">
                      <a
                        href={config.calendar_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 font-medium"
                      >
                        <ExternalLink className="w-4 h-4" />
                        Test your calendar link
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Help Text */}
              <div className="mt-6 p-4 bg-blue-50 rounded-xl border border-blue-100">
                <h4 className="text-sm font-medium text-blue-900 mb-1">How it works</h4>
                <ul className="text-xs text-blue-700 space-y-1">
                  <li>• Visitors click the button on your profile</li>
                  <li>• They're redirected to your calendar to book</li>
                  <li>• Use the private link for invite-only booking</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
